import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/common";
import { Building2, Plus, Layers, Users, Clock, UserCheck, Network, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";

function OrgLanguages() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const [langs, setLangs] = useState(user?.company_languages || ["sv", "en"]);
  const toggle = async (l, on) => {
    const next = on ? [...new Set([...langs, l])] : langs.filter((x) => x !== l);
    if (next.length === 0) return toast.error(t("min_one_lang"));
    try { const r = await api.patch("/auth/company/languages", { languages: next }); setLangs(r.data.languages); refreshUser && refreshUser(); toast.success(t("save")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-4 flex flex-wrap items-center gap-4 p-4" data-testid="org-languages">
      <div className="text-sm font-semibold">{t("org_languages")}</div>
      {["sv", "en"].map((l) => (
        <label key={l} className="flex items-center gap-2 text-sm" data-testid={`org-lang-${l}`}>
          <Checkbox checked={langs.includes(l)} onCheckedChange={(v) => toggle(l, !!v)} /> {t(l === "sv" ? "lang_sv" : "lang_en")}
        </label>
      ))}
      <span className="text-xs text-muted-foreground">{t("org_languages_hint")}</span>
    </Card>
  );
}

function RespPicker({ users, value, onChange, testid }) {
  const { t } = useLang();
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={t("resp_ph")} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">{t("no_resp")}</SelectItem>
        {(users || []).map((u) => <SelectItem key={u.id} value={u.email}>{u.name} · {u.role_label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function RespBadge({ users, email }) {
  const { t } = useLang();
  const u = (users || []).find((x) => x.email === email);
  if (!email) return <Badge variant="outline" className="text-muted-foreground">{t("no_resp")}</Badge>;
  return <Badge variant="secondary" className="gap-1"><UserCheck className="h-3 w-3" />{u ? u.name : email}</Badge>;
}

function RotationToggle() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const [on, setOn] = useState(user?.company_rotation_enabled !== false);
  const toggle = async (v) => {
    try { await api.patch("/auth/company/rotation", { enabled: v }); setOn(v); refreshUser && refreshUser(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-4 flex flex-wrap items-center gap-4 p-4" data-testid="rotation-toggle">
      <div className="text-sm font-semibold">{t("org_rotation")}</div>
      <label className="flex items-center gap-2 text-sm" data-testid="rotation-label">
        <Checkbox checked={on} onCheckedChange={(v) => toggle(!!v)} data-testid="rotation-checkbox" /> {t("org_rotation_toggle")}
      </label>
      <span className="text-xs text-muted-foreground">{t("org_rotation_hint")}</span>
    </Card>
  );
}

export default function Organisation() {
  const { t } = useLang();
  const { data: facilities, reload: rF } = useApi("/facilities");
  const { data: areas, reload: rA } = useApi("/business_areas");
  const { data: departments, reload: rD } = useApi("/departments");
  const { data: teams, reload: rT } = useApi("/teams");
  const { data: shifts, reload: rS } = useApi("/shifts");
  const { data: users } = useApi("/auth/users");

  const [fac, setFac] = useState({ name: "" });
  const [area, setArea] = useState({ name: "", description: "", type: "bemanning" });
  const [delArea, setDelArea] = useState(null);
  const AREA_TYPES = { bemanning: t("area_type_bemanning"), administration: t("area_type_admin") };
  const [dep, setDep] = useState({ name: "", facility_id: "", business_area_id: "", responsible_id: "", approver_id: "" });
  const [team, setTeam] = useState({ name: "", department_id: "", responsible_id: "", shift_id: "" });
  const [shift, setShift] = useState({ name: "", department_id: "", start_time: "", end_time: "", responsible_id: "" });

  const facName = (id) => (facilities || []).find((f) => f.id === id)?.name || "—";
  const areaName = (id) => (areas || []).find((a) => a.id === id)?.name;
  const depName = (id) => (departments || []).find((d) => d.id === id)?.name || "—";

  const create = async (coll, body, reset, reload) => {
    try { await api.post(`/${coll}`, body); reset(); reload(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const removeArea = async (force) => {
    try {
      await api.delete(`/business_areas/${delArea.id}${force ? "?force=true" : ""}`);
      setDelArea(null); rA(); rD();
      toast.success(t("area_removed"));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delAreaLinked = delArea ? (departments || []).filter((d) => d.business_area_id === delArea.id).length : 0;

  return (
    <div>
      <PageHeader title={t("org_title")} icon={Building2} subtitle={t("org_sub")} />
      <OrgLanguages />
      <RotationToggle />
      <Tabs defaultValue="dep">
        <TabsList>
          <TabsTrigger value="fac" data-testid="tab-fac"><Building2 className="mr-1 h-4 w-4" />{t("tab_facilities")}</TabsTrigger>
          <TabsTrigger value="area" data-testid="tab-area"><Network className="mr-1 h-4 w-4" />{t("tab_areas")}</TabsTrigger>
          <TabsTrigger value="dep" data-testid="tab-dep"><Layers className="mr-1 h-4 w-4" />{t("tab_depts")}</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team"><Users className="mr-1 h-4 w-4" />{t("tab_teams")}</TabsTrigger>
          <TabsTrigger value="shift" data-testid="tab-shift"><Clock className="mr-1 h-4 w-4" />{t("tab_shifts")}</TabsTrigger>
        </TabsList>

        {/* Anläggningar */}
        <TabsContent value="fac" className="mt-4">
          <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
            <div className="min-w-[220px] flex-1"><Label>{t("fac_name_lbl")}</Label>
              <Input value={fac.name} onChange={(e) => setFac({ name: e.target.value })} data-testid="fac-name" /></div>
            <Button onClick={() => create("facilities", fac, () => setFac({ name: "" }), rF)} disabled={!fac.name} data-testid="add-fac"><Plus className="mr-1 h-4 w-4" />{t("add")}</Button>
          </Card>
          {(facilities || []).length === 0 ? <EmptyState icon={Building2} title={t("no_facilities")} text={t("no_facilities_text")} /> :
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {facilities.map((f) => <Card key={f.id} className="p-4 font-medium" data-testid={`fac-${f.id}`}>{f.name}</Card>)}
            </div>}
        </TabsContent>

        {/* Verksamhetsområden */}
        <TabsContent value="area" className="mt-4">
          <Card className="mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <div><Label>{t("area_name_lbl")}</Label>
              <Input value={area.name} onChange={(e) => setArea((a) => ({ ...a, name: e.target.value }))} placeholder={t("area_name_ph")} data-testid="area-name" /></div>
            <div><Label>{t("type_lbl")}</Label>
              <Select value={area.type} onValueChange={(v) => setArea((a) => ({ ...a, type: v }))}>
                <SelectTrigger data-testid="area-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(AREA_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>{t("desc_opt_lbl")}</Label>
              <Input value={area.description} onChange={(e) => setArea((a) => ({ ...a, description: e.target.value }))} placeholder={t("desc_ph")} data-testid="area-desc" /></div>
            <div className="lg:col-span-3"><Button onClick={() => create("business_areas", area, () => setArea({ name: "", description: "", type: "bemanning" }), rA)} disabled={!area.name} data-testid="add-area"><Plus className="mr-1 h-4 w-4" />{t("add_area")}</Button></div>
          </Card>
          {(areas || []).length === 0 ? <EmptyState icon={Network} title={t("no_areas")} text={t("no_areas_text")} /> :
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {areas.map((a) => {
                const count = (departments || []).filter((d) => d.business_area_id === a.id).length;
                return (
                  <Card key={a.id} className="p-4" data-testid={`area-${a.id}`}>
                    <div className="flex items-center gap-2"><Network className="h-4 w-4 text-accent" /><span className="font-medium">{a.name}</span>
                      <Badge variant="secondary" className="ml-auto">{t("n_depts", { n: count })}</Badge>
                      <button onClick={() => setDelArea(a)} data-testid={`del-area-${a.id}`} title={t("delete")}><Trash2 className="h-4 w-4 text-red-500" /></button></div>
                    <Badge variant={a.type === "administration" ? "outline" : "default"} className="mt-2">{AREA_TYPES[a.type] || AREA_TYPES.bemanning}</Badge>
                    {a.description && <p className="mt-1 text-xs text-muted-foreground">{a.description}</p>}
                  </Card>
                );
              })}
            </div>}
          <Dialog open={!!delArea} onOpenChange={(o) => !o && setDelArea(null)}>
            <DialogContent data-testid="del-area-dialog">
              <DialogHeader><DialogTitle>{t("del_title", { name: delArea?.name })}</DialogTitle></DialogHeader>
              {delAreaLinked > 0 ? (
                <p className="text-sm text-muted-foreground">{t("del_area_linked", { n: delAreaLinked })}</p>
              ) : (
                <p className="text-sm text-muted-foreground">{t("del_area_none")}</p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDelArea(null)} data-testid="del-area-cancel">{t("cancel")}</Button>
                <Button variant="destructive" onClick={() => removeArea(delAreaLinked > 0)} data-testid="del-area-confirm">
                  {delAreaLinked > 0 ? t("del_remove_anyway") : t("delete")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Avdelningar */}
        <TabsContent value="dep" className="mt-4">
          <Card className="mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div><Label>{t("dep_name_lbl")}</Label><Input value={dep.name} onChange={(e) => setDep((d) => ({ ...d, name: e.target.value }))} data-testid="dep-name" /></div>
            <div><Label>{t("facility_lbl")}</Label>
              <Select value={dep.facility_id || "none"} onValueChange={(v) => setDep((d) => ({ ...d, facility_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="dep-facility"><SelectValue placeholder={t("choose")} /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(facilities || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>{t("area_lbl")}</Label>
              <Select value={dep.business_area_id || "none"} onValueChange={(v) => setDep((d) => ({ ...d, business_area_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="dep-area"><SelectValue placeholder={t("choose")} /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(areas || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>{t("resp_chef")}</Label><RespPicker users={users} value={dep.responsible_id} onChange={(v) => setDep((d) => ({ ...d, responsible_id: v }))} testid="dep-responsible" /></div>
            <div><Label>{t("resp_approver")}</Label><RespPicker users={users} value={dep.approver_id} onChange={(v) => setDep((d) => ({ ...d, approver_id: v }))} testid="dep-approver" /></div>
            <div className="lg:col-span-4"><Button onClick={() => create("departments", dep, () => setDep({ name: "", facility_id: "", business_area_id: "", responsible_id: "", approver_id: "" }), rD)} disabled={!dep.name} data-testid="add-dep"><Plus className="mr-1 h-4 w-4" />{t("add_dep")}</Button></div>
          </Card>
          {(departments || []).length === 0 ? <EmptyState icon={Layers} title={t("no_deps")} text={t("no_deps_text")} /> :
            <div className="space-y-2">
              {departments.map((d) => (
                <Card key={d.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`dep-${d.id}`}>
                  <Layers className="h-5 w-5 text-accent" />
                  <div className="flex-1"><div className="font-medium">{d.name}</div><div className="text-xs text-muted-foreground">{facName(d.facility_id)}{areaName(d.business_area_id) && ` · ${areaName(d.business_area_id)}`}</div></div>
                  <RespBadge users={users} email={d.responsible_id} />
                  {d.approver_id && <Badge variant="outline">{t("approver_prefix", { name: (users || []).find((u) => u.email === d.approver_id)?.name || d.approver_id })}</Badge>}
                </Card>
              ))}
            </div>}
        </TabsContent>

        {/* Team */}
        <TabsContent value="team" className="mt-4">
          <Card className="mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div><Label>{t("team_name_lbl")}</Label><Input value={team.name} onChange={(e) => setTeam((tm) => ({ ...tm, name: e.target.value }))} data-testid="team-name" /></div>
            <div><Label>{t("dep_lbl")}</Label>
              <Select value={team.department_id || "none"} onValueChange={(v) => setTeam((tm) => ({ ...tm, department_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="team-dep"><SelectValue placeholder={t("choose")} /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(departments || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>{t("team_leader_lbl")}</Label><RespPicker users={users} value={team.responsible_id} onChange={(v) => setTeam((tm) => ({ ...tm, responsible_id: v }))} testid="team-responsible" /></div>
            <div><Label>{t("shift_opt_lbl")}</Label>
              <Select value={team.shift_id || "none"} onValueChange={(v) => setTeam((tm) => ({ ...tm, shift_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="team-shift"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(shifts || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="lg:col-span-4"><Button onClick={() => create("teams", team, () => setTeam({ name: "", department_id: "", responsible_id: "", shift_id: "" }), rT)} disabled={!team.name} data-testid="add-team"><Plus className="mr-1 h-4 w-4" />{t("add_team")}</Button></div>
          </Card>
          {(teams || []).length === 0 ? <EmptyState icon={Users} title={t("no_teams")} text={t("no_teams_text")} /> :
            <div className="space-y-2">
              {teams.map((tm) => (
                <Card key={tm.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`team-${tm.id}`}>
                  <Users className="h-5 w-5 text-accent" />
                  <div className="flex-1"><div className="font-medium">{tm.name}</div><div className="text-xs text-muted-foreground">{depName(tm.department_id)}{tm.shift_id && ` · ${(shifts || []).find((s) => s.id === tm.shift_id)?.name || ""}`}</div></div>
                  <RespBadge users={users} email={tm.responsible_id} />
                </Card>
              ))}
            </div>}
        </TabsContent>

        {/* Skift */}
        <TabsContent value="shift" className="mt-4">
          <Card className="mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
            <div><Label>{t("shift_name_lbl")}</Label><Input value={shift.name} onChange={(e) => setShift((s) => ({ ...s, name: e.target.value }))} placeholder={t("shift_name_ph")} data-testid="shift-name" /></div>
            <div><Label>{t("dep_lbl")}</Label>
              <Select value={shift.department_id || "none"} onValueChange={(v) => setShift((s) => ({ ...s, department_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="shift-dep"><SelectValue placeholder={t("choose")} /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(departments || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>{t("start_lbl")}</Label><Input type="time" value={shift.start_time} onChange={(e) => setShift((s) => ({ ...s, start_time: e.target.value }))} data-testid="shift-start" /></div>
            <div><Label>{t("end_lbl")}</Label><Input type="time" value={shift.end_time} onChange={(e) => setShift((s) => ({ ...s, end_time: e.target.value }))} data-testid="shift-end" /></div>
            <div><Label>{t("resp_lbl")}</Label><RespPicker users={users} value={shift.responsible_id} onChange={(v) => setShift((s) => ({ ...s, responsible_id: v }))} testid="shift-responsible" /></div>
            <div className="lg:col-span-5"><Button onClick={() => create("shifts", shift, () => setShift({ name: "", department_id: "", start_time: "", end_time: "", responsible_id: "" }), rS)} disabled={!shift.name} data-testid="add-shift"><Plus className="mr-1 h-4 w-4" />{t("add_shift")}</Button></div>
          </Card>
          {(shifts || []).length === 0 ? <EmptyState icon={Clock} title={t("no_shifts")} text={t("no_shifts_text")} /> :
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {shifts.map((s) => (
                <Card key={s.id} className="p-4" data-testid={`shift-${s.id}`}>
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-accent" /><span className="font-medium">{s.name}</span>
                    <span className="ml-auto font-mono text-xs text-muted-foreground">{s.start_time}–{s.end_time}</span></div>
                  <div className="mt-1 text-xs text-muted-foreground">{depName(s.department_id)}</div>
                  <div className="mt-2"><RespBadge users={users} email={s.responsible_id} /></div>
                </Card>
              ))}
            </div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
